/**
 * DeliveryRequestForm - Quick request form for reps
 * 
 * Features:
 * - PIN protected access
 * - No login required
 * - Submit delivery/collection requests directly to whiteboard
 * - Mobile-friendly design
 * 
 * Usage: /request-delivery
 */
import { useState } from "react";
import axios from "axios";
import { 
  Truck, Package, Calendar, Clock, User, FileText, MapPin,
  CheckCircle, AlertTriangle, Lock, Send, ArrowLeft, ShoppingBag, Store
} from "lucide-react";
import { PICKUP_POINTS, requestTypeLabel } from "./requestTypes";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = [
  { id: 'monday', label: 'Monday' },
  { id: 'tuesday', label: 'Tuesday' },
  { id: 'wednesday', label: 'Wednesday' },
  { id: 'thursday', label: 'Thursday' },
  { id: 'friday', label: 'Friday' }
];

const TIME_SLOTS = [
  { id: '', label: 'Any Time' },
  { id: 'AM', label: 'Morning (AM)' },
  { id: 'PM', label: 'Afternoon (PM)' },
  { id: '9:00', label: '9:00 AM' },
  { id: '10:00', label: '10:00 AM' },
  { id: '11:00', label: '11:00 AM' },
  { id: '12:00', label: '12:00 PM' },
  { id: '13:00', label: '1:00 PM' },
  { id: '14:00', label: '2:00 PM' },
  { id: '15:00', label: '3:00 PM' },
  { id: '16:00', label: '4:00 PM' }
];

const DeliveryRequestForm = () => {
  const [pin, setPin] = useState('');
  const [pinVerified, setPinVerified] = useState(false);
  const [pinError, setPinError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  
  // Form fields
  const [requestType, setRequestType] = useState('delivery');
  const [pickupPoint, setPickupPoint] = useState('');
  const [jobNumber, setJobNumber] = useState('');
  const [customer, setCustomer] = useState('');
  const [location, setLocation] = useState('');
  const [day, setDay] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  const [notes, setNotes] = useState('');
  const [requestedBy, setRequestedBy] = useState('');
  
  const [error, setError] = useState('');
  const isClickCollect = requestType === 'click_collect';
  const typeLabel = requestTypeLabel(requestType);

  // Verify PIN
  const verifyPin = async () => {
    setLoading(true);
    setPinError(false);
    try {
      const response = await axios.get(`${API}/delivery-request/verify-pin?pin=${pin}`);
      if (response.data.valid) {
        setPinVerified(true);
      } else {
        setPinError(true);
      }
    } catch (err) {
      setPinError(true);
    } finally {
      setLoading(false);
    }
  };

  // Submit request
  const submitRequest = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!customer.trim()) {
      setError('Customer name is required');
      return;
    }
    if (!day) {
      setError('Please select a day');
      return;
    }
    if (isClickCollect && !pickupPoint) {
      setError('Please choose a pickup point');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/delivery-request`, {
        pin,
        job_number: jobNumber.trim(),
        customer: customer.trim(),
        request_type: requestType,
        pickup_point: isClickCollect ? pickupPoint : '',
        day,
        time_slot: timeSlot,
        location: isClickCollect ? '' : location.trim(),
        notes: notes.trim(),
        requested_by: requestedBy.trim()
      });
      
      setSubmittedData(response.data);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  // Reset form for another request
  const resetForm = () => {
    setJobNumber('');
    setCustomer('');
    setPickupPoint('');
    setDay('');
    setTimeSlot('');
    setNotes('');
    setSubmitted(false);
    setSubmittedData(null);
    setError('');
  };

  // PIN Entry Screen
  if (!pinVerified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Truck className="w-10 h-10 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Delivery Request</h1>
            <p className="text-gray-500 mt-2">Enter PIN to continue</p>
          </div>
          
          <div className="space-y-4">
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && verifyPin()}
                placeholder="Enter PIN"
                className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl text-lg text-gray-900 placeholder-gray-400 focus:outline-none transition-colors ${
                  pinError ? 'border-red-500 bg-red-50' : 'border-gray-200 focus:border-blue-500'
                }`}
                autoFocus
              />
            </div>
            
            {pinError && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertTriangle className="w-4 h-4" />
                <span>Invalid PIN. Please try again.</span>
              </div>
            )}
            
            <button
              onClick={verifyPin}
              disabled={loading || !pin}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <span>Continue</span>
                  <ArrowLeft className="w-5 h-5 rotate-180" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success Screen
  if (submitted && submittedData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Request Submitted!</h1>
          <p className="text-gray-500 mb-6">
            Your {typeLabel} request has been added to the whiteboard.
          </p>
          
          <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-500">Job/Reference</p>
                <p className="font-bold text-gray-800">{submittedData.job_id}</p>
              </div>
              <div>
                <p className="text-gray-500">Day</p>
                <p className="font-bold text-gray-800 capitalize">{submittedData.day}</p>
              </div>
              <div>
                <p className="text-gray-500">Customer</p>
                <p className="font-bold text-gray-800">{customer}</p>
              </div>
              <div>
                <p className="text-gray-500">Type</p>
                <p className="font-bold text-gray-800" data-testid="request-success-type">{typeLabel}</p>
              </div>
              {isClickCollect && (
                <div className="col-span-2">
                  <p className="text-gray-500">Pickup Point</p>
                  <p className="font-bold text-orange-700" data-testid="request-success-pickup">{pickupPoint}</p>
                </div>
              )}
            </div>
          </div>
          
          <button
            onClick={resetForm}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-xl transition-colors"
          >
            Submit Another Request
          </button>
        </div>
      </div>
    );
  }

  // Request Form
  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
            <Truck className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Delivery Request</h1>
          <p className="text-gray-500">Submit a delivery, collection or click & collect request</p>
        </div>

        {/* Form */}
        <form onSubmit={submitRequest} className="bg-white rounded-2xl shadow-lg p-6 space-y-5">
          
          {/* Request Type Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Request Type</label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setRequestType('delivery')}
                data-testid="request-type-delivery"
                className={`flex items-center justify-center gap-1.5 py-3 px-2 rounded-xl font-bold text-sm transition-all ${
                  requestType === 'delivery'
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Truck className="w-5 h-5 flex-shrink-0" />
                Delivery
              </button>
              <button
                type="button"
                onClick={() => setRequestType('collection')}
                data-testid="request-type-collection"
                className={`flex items-center justify-center gap-1.5 py-3 px-2 rounded-xl font-bold text-sm transition-all ${
                  requestType === 'collection'
                    ? 'bg-gray-700 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Package className="w-5 h-5 flex-shrink-0" />
                Collection
              </button>
              <button
                type="button"
                onClick={() => setRequestType('click_collect')}
                data-testid="request-type-click-collect"
                className={`flex items-center justify-center gap-1.5 py-3 px-2 rounded-xl font-bold text-sm transition-all ${
                  isClickCollect
                    ? 'bg-orange-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <ShoppingBag className="w-5 h-5 flex-shrink-0" />
                <span className="leading-tight text-left">Click &amp; Collect</span>
              </button>
            </div>
          </div>

          {/* Pickup Point (Click & Collect only) */}
          {isClickCollect && (
            <div data-testid="pickup-point-field">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Factory Pickup Point <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {PICKUP_POINTS.map((pp) => (
                  <button
                    key={pp}
                    type="button"
                    onClick={() => setPickupPoint(pp)}
                    data-testid={`pickup-point-${pp.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className={`flex items-center gap-2 px-3 py-3 rounded-xl border-2 text-sm font-semibold text-left transition-all ${
                      pickupPoint === pp
                        ? 'border-orange-500 bg-orange-50 text-orange-800'
                        : 'border-gray-200 text-gray-600 hover:border-orange-300'
                    }`}
                  >
                    <Store className={`w-4 h-4 flex-shrink-0 ${pickupPoint === pp ? 'text-orange-600' : 'text-gray-400'}`} />
                    {pp}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Job Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Number <span className="text-gray-400">(optional)</span>
            </label>
            <div className="relative">
              <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={jobNumber}
                onChange={(e) => setJobNumber(e.target.value)}
                placeholder="e.g. 67835"
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors text-gray-900 placeholder-gray-400"
              />
            </div>
          </div>

          {/* Customer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                placeholder="Customer name"
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors text-gray-900 placeholder-gray-400"
                required
              />
            </div>
          </div>

          {/* Delivery Location (not for Click & Collect) */}
          {!isClickCollect && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {requestType === 'collection' ? 'Collection' : 'Delivery'} Location <span className="text-gray-400">(optional)</span>
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. Santry"
                data-testid="delivery-location-input"
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors text-gray-900 placeholder-gray-400"
              />
            </div>
          </div>
          )}

          {/* Day Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Day Required <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={day}
                onChange={(e) => setDay(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors appearance-none bg-white text-gray-900"
                required
              >
                <option value="">Select a day...</option>
                {DAYS.map(d => (
                  <option key={d.id} value={d.id}>{d.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Time Slot */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Preference <span className="text-gray-400">(optional)</span>
            </label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={timeSlot}
                onChange={(e) => setTimeSlot(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors appearance-none bg-white text-gray-900"
              >
                {TIME_SLOTS.map(t => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any special instructions or details..."
              rows={3}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors resize-none text-gray-900 placeholder-gray-400"
            />
          </div>

          {/* Requested By */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Name <span className="text-gray-400">(optional)</span>
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={requestedBy}
                onChange={(e) => setRequestedBy(e.target.value)}
                placeholder="Who's making this request?"
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors text-gray-900 placeholder-gray-400"
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-xl">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            data-testid="request-submit-btn"
            className={`w-full py-4 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-2 ${
              requestType === 'delivery'
                ? 'bg-blue-600 hover:bg-blue-700'
                : isClickCollect
                  ? 'bg-orange-500 hover:bg-orange-600'
                  : 'bg-gray-700 hover:bg-gray-800'
            } disabled:bg-gray-300`}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Send className="w-5 h-5" />
                Submit {typeLabel} Request
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <p className="text-center text-gray-400 text-sm mt-6">
          Requests go directly to the Delivery Whiteboard
        </p>
      </div>
    </div>
  );
};

export default DeliveryRequestForm;
